// useInitializeViewerState — extrahiert aus
// app/src/components/editor/Viewer/useInitializeViewerState.ts
// (Plan A Mapping-Audit 2026-05-22).
//
// Abstrahiert app/-Coupling:
//   - `env.imgproxyUrl` → `options.imgproxyUrl` Parameter
//   - `track.storyNavigation` (Google Analytics) → `options.onEvent` Callback
//   - `useResizeObserver` → lokaler Import (gleicher Package-Pfad)
//
// Renderer-Lifecycle bleibt 1:1 wie in app/: bei Mount Renderer + Store +
// PlayablePresentation initialisieren, start() läuft, aber Slide-Navigation
// erst wenn scrollTop > 0 (was useBlockVertScroll bis User-Click verhindert).

import type { Dispatch, RefObject, SetStateAction } from "react";
import { useCallback, useEffect, useRef } from "react";
import {
  Renderer,
  VisualStoryStore,
  RendererStore,
  PlayablePresentation,
  WebGLAssets,
  createImageOptimizer,
} from "@scrolly/renderer";
import type {
  RendererProps,
  VisualStoryEventHandler,
} from "@scrolly/renderer";
import type { CustomFonts, PostJson } from "@scrolly/post-types";
import useResizeObserver from "./useResizeObserver";

export interface UseInitializeViewerStateOptions {
  /**
   * imgproxy base URL für Image-Optimization. Leer-String oder undefined
   * fällt auf raw Asset-URL zurück. In app/ aus `env.imgproxyUrl`, in
   * public/ aus `import.meta.env.VITE_IMGPROXY_URL`.
   */
  imgproxyUrl?: string;
  /**
   * Analytics-Callback für Renderer-Events (heute nur "storyNavigation").
   * Wenn undefined: No-Op. app/ bridged das auf Google Analytics dataLayer
   * via `track.storyNavigation`. public/ konsumiert (heute) nicht, kann
   * in Plan-2 eigene Tracking-Pipeline einhaken.
   */
  onEvent?: VisualStoryEventHandler;
  /**
   * Scroll-Source-Polarität (Plan-2 iOS-Fix). DEFAULT "container" (heute).
   * Nur public/-Immersive-Full-Page-Reader → "window" (window.scrollY).
   */
  scrollSource?: "container" | "window";
  /** Editor template preview — show full bloom quote text while editing. */
  editorPreview?: boolean;
  /** Editor: hide Sentence-Bloom SVG while the Titel & Hintergrund panel is active. */
  sentenceBloomCoverPreview?: boolean;
}

interface ViewerState {
  onResize: () => void;
  renderer: RefObject<Renderer | null>;
  visualStoryStore: RefObject<VisualStoryStore | null>;
  mainContainerRef: RefObject<HTMLDivElement | null>;
  canvasContainerRef: RefObject<HTMLDivElement | null>;
  htmlRendererRef: RefObject<HTMLDivElement | null>;
  viewerRootContainerRef: RefObject<HTMLElement | null>;
}

export default function useInitializeViewerState(
  id: string | number,
  post: PostJson,
  setProgress: Dispatch<SetStateAction<number>>,
  setCurrentSlideIndex: Dispatch<SetStateAction<number>>,
  setStarted: Dispatch<SetStateAction<boolean>>,
  viewerRootContainerRef: RefObject<HTMLElement | null>,
  heightRef: RefObject<HTMLElement | null>,
  fontFamilies: CustomFonts,
  options?: UseInitializeViewerStateOptions,
): ViewerState {
  const renderer = useRef<Renderer>(null);
  const initializedId = useRef<string | number>(null);
  const visualStoryStore = useRef<VisualStoryStore>(null);
  const playablePresentationRef = useRef<PlayablePresentation | null>(null);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const htmlRendererRef = useRef<HTMLDivElement>(null);

  const imgproxyUrl = options?.imgproxyUrl ?? "";

  // Stable-ref pattern: consumer-supplied onEvent darf zwischen Renders
  // wechseln (z.B. inline-arrow), aber unsere useEffect-Dependency soll
  // dadurch nicht das Setup neu triggern. Wir lesen aus ref bei jedem
  // Event-Fire.
  const onEventRef = useRef(options?.onEvent);
  useEffect(() => {
    onEventRef.current = options?.onEvent;
  });

  // Editor panel toggles this each render — must not be captured once in init.
  const sentenceBloomCoverPreviewRef = useRef(
    options?.sentenceBloomCoverPreview ?? false,
  );
  useEffect(() => {
    sentenceBloomCoverPreviewRef.current =
      options?.sentenceBloomCoverPreview ?? false;
  });

  const isCoverActive = useCallback((): boolean => {
    if (sentenceBloomCoverPreviewRef.current) return true;
    const store = visualStoryStore.current;
    if (!store) return true;
    if (store.innerHeight <= 0) return true;
    return store.beforeStory;
  }, []);

  const bridgedEvent: VisualStoryEventHandler = useCallback(
    (name, payload) => {
      onEventRef.current?.(name, payload);
    },
    [],
  );

  const stopCallback = useCallback(() => {
    // No-op placeholder — Renderer expects a stop()-callback but the actual
    // teardown happens via stopViewer() in cleanup.
  }, []);

  // Read visualStoryStore.current at CALL time, not closure time. Previously
  // captured at render time (null on first render), causing onResize() to
  // silently no-op during initialization and viewport toggle.
  const onResize = useCallback(() => {
    const storyStore = visualStoryStore.current;
    if (storyStore && renderer.current) {
      storyStore.regenerateBoundsCache();
      const rendererStore = renderer.current.props.renderer;
      const height = storyStore.innerHeight;
      const width = storyStore.innerWidth;
      rendererStore.setHeight(height);
      rendererStore.setWidth(width);
      rendererStore.setBounds(height, width, 0, 0);
      storyStore.regenerateSlidePositionsCache();
    }
  }, []);

  const stopViewer = useCallback(() => {
    playablePresentationRef.current?.slidePlayer?.stopLoop();
    playablePresentationRef.current?.stop();
    visualStoryStore.current?.stop();

    visualStoryStore.current = null;
    playablePresentationRef.current = null;

    if (renderer.current) {
      renderer.current.stop();
      renderer.current.dispose();
      renderer.current.webGLRenderer?.domElement.remove();
      renderer.current = null;
    }

    WebGLAssets.layerIndex = 0;
    WebGLAssets.allLayersSet = false;
    WebGLAssets.setImagesOnStart = false;
    WebGLAssets.setVideosOnStart = false;
    WebGLAssets.setGpuTextOnStart = false;
  }, []);

  useEffect(() => {
    return () => {
      stopViewer();
    };
  }, [stopViewer]);

  useEffect(() => {
    const imageOptimizer = createImageOptimizer(imgproxyUrl);

    if (
      renderer.current === null &&
      canvasContainerRef.current &&
      htmlRendererRef.current &&
      mainContainerRef.current &&
      viewerRootContainerRef.current &&
      heightRef.current
    ) {
      const rendererStore = new RendererStore((newProgress) => {
        setProgress(newProgress);
      });
      const playablePresentation = new PlayablePresentation(
        post.data.data,
        rendererStore,
      );
      playablePresentation.slidePlayer.setOnSlideUpdate(setCurrentSlideIndex);
      const rendererProps: RendererProps = {
        isProduction: true,
        stop: stopCallback,
        start: (animate) => {
          playablePresentation.slidePlayer.startLoop(animate);
        },
        renderer: playablePresentation.renderer,
        slidePlayer: playablePresentation.slidePlayer,
        presentation: playablePresentation.presentationRender,
        isCoverActive,
      };

      if (visualStoryStore.current === null) {
        visualStoryStore.current = new VisualStoryStore(
          viewerRootContainerRef.current,
          heightRef.current,
          bridgedEvent,
          options?.scrollSource ?? "container",
        );
      }
      visualStoryStore.current.setPost(playablePresentation);
      renderer.current = new Renderer(
        canvasContainerRef.current,
        htmlRendererRef.current,
        rendererProps,
        imageOptimizer,
      );
      renderer.current.getFonts(fontFamilies);
      onResize();
      renderer.current.start();
      rendererProps.renderer.startFunction();
      visualStoryStore.current.start(mainContainerRef.current);
      playablePresentationRef.current = playablePresentation;
      initializedId.current = id;

      // Plan-N2 Phase 2 v6 Diagnose-Bridge (2026-05-25 Bene): Live-DOM-
      // Inspect-Hook MIT Auto-Logger. Aktiv nur wenn `?debug=1` im URL.
      //
      // Befund v5: `presentationRender.layers` existiert nicht — der
      // korrekte Pfad ist `playablePresentation.presentationSerialized.
      // layers` (flat Liste aus PostJson, alle Video-Layer mit from/to-
      // Werten). presentationRender hat nur groupTree/slides/options.
      //
      // Double-Init-Fix: wenn ein vorheriger Logger lebt (z.B. weil
      // useEffect re-run wegen dependency-array-change), stoppen wir
      // ihn bevor ein neuer startet. Verhindert die "doppelt-loggt mit
      // clientHeight:0"-Artefakte.
      if (
        typeof window !== "undefined" &&
        window.location?.search?.includes("debug=1")
      ) {
        // Cleanup vorherigen Logger falls vorhanden (Double-Init-Schutz)
        const prevStop = (
          window as unknown as { __scrollyDebugStop?: () => void }
        ).__scrollyDebugStop;
        if (prevStop) {
          try {
            prevStop();
          } catch {
            // ignore
          }
        }

        const debugRefs = {
          visualStoryStore: visualStoryStore.current,
          slidePlayer: playablePresentation.slidePlayer,
          presentationSerialized: playablePresentation.presentationSerialized,
          presentationRender: playablePresentation.presentationRender,
          playablePresentation,
          renderer: renderer.current,
          scrollEl: viewerRootContainerRef.current,
          heightEl: heightRef.current,
          mainEl: mainContainerRef.current,
        };
        (window as unknown as { __scrollyDebug?: unknown }).__scrollyDebug =
          debugRefs;
        // eslint-disable-next-line no-console
        console.log(
          "[scrolly-debug] bridge ready (v6) — Auto-Logger startet in 500ms",
          { keys: Object.keys(debugRefs) },
        );

        // Layer-Counts initial loggen — JETZT MIT MARKERN
        // (isBackground, playbackMode) damit wir unterscheiden können
        // zwischen intentional Multi-Slide (Background/Loop/Scroll-Mode)
        // und kaputten PPI-Lifetimes.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const allLayers =
          (debugRefs.presentationSerialized as any)?.layers ?? [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const initialVideoLayers = allLayers.filter(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (l: any) => l?.type === "video",
        );
        // eslint-disable-next-line no-console
        console.log("[scrolly-debug] layer-counts", {
          allLayersTotal: allLayers.length,
          videoLayersTotal: initialVideoLayers.length,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          videoLayersList: initialVideoLayers.map((l: any) => ({
            id: typeof l.id === "string" ? l.id.slice(0, 16) : "?",
            from: l.from,
            to: l.to,
            slideId: l.slideId,
            isBackground: l.isBackground === true,
            playbackMode: l.content?.playbackMode ?? "loop(default)",
            loop: l.content?.loop,
            muted: l.content?.muted,
            transparent: l.content?.transparent,
          })),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          slides: (debugRefs.presentationSerialized as any)?.slides?.map(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (s: any) => ({
              id: typeof s.id === "string" ? s.id.slice(0, 16) : "?",
              from: s.from,
              to: s.to,
            }),
          ),
        });

        const startTime = Date.now();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const intervalId = setInterval(() => {
          if (Date.now() - startTime > 60_000) {
            clearInterval(intervalId);
            // eslint-disable-next-line no-console
            console.log("[scrolly-debug] auto-logger stopped (60s)");
            return;
          }
          try {
            const sc = debugRefs.scrollEl;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const store = debugRefs.visualStoryStore as any;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const sp = debugRefs.slidePlayer as any;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const layers =
              (debugRefs.presentationSerialized as any)?.layers ?? [];
            const timer = sp?.timer;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const videos = layers
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .filter((l: any) => l?.type === "video")
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .map((l: any) => ({
                id: typeof l.id === "string" ? l.id.slice(0, 12) : "?",
                from: l.from,
                to: l.to,
                isBackground: l.isBackground === true,
                playbackMode: l.content?.playbackMode ?? "loop(default)",
                inLifetime:
                  typeof timer === "number" &&
                  l.from <= timer &&
                  l.to >= timer,
              }));
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const inLife = videos.filter((v: any) => v.inLifetime);

            // Plan-N2 Block 3 v9: Slide-Gate-Diagnose — zeige
            // currentSlide-Index vs layerOwnSlide-Index pro Video.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const presSlides =
              (debugRefs.presentationRender as any)?.slides ?? [];
            const currentSlide = sp?.currentSlide;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const slideGateInfo = inLife.map((v: any) => {
              let ownSlideIdx = -1;
              for (let i = 0; i < presSlides.length; i++) {
                const start = presSlides[i].from;
                const end =
                  i + 1 < presSlides.length
                    ? presSlides[i + 1].from
                    : Infinity;
                if (start <= v.from && v.from < end) {
                  ownSlideIdx = i;
                  break;
                }
              }
              return {
                videoId: v.id,
                videoFrom: v.from,
                ownSlideIdx,
                currentSlide,
                isOnOwnSlide: ownSlideIdx === currentSlide,
              };
            });

            // eslint-disable-next-line no-console
            console.log("[scrolly-debug]", {
              clientHeight: sc?.clientHeight,
              scrollTop_DOM: sc?.scrollTop,
              scrollTop_store: store?.scrollTop,
              timer,
              currentSlide,
              slidesCount: presSlides.length,
              videosInLifetime: inLife,
              inLifetimeCount: inLife.length,
              slideGateInfo,
            });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error("[scrolly-debug] logger error:", e);
          }
        }, 500);

        // Expose stop-function on window für manuelles Stop + Double-Init-
        // Cleanup beim nächsten Mount.
        (
          window as unknown as { __scrollyDebugStop?: () => void }
        ).__scrollyDebugStop = () => {
          clearInterval(intervalId);
          // eslint-disable-next-line no-console
          console.log("[scrolly-debug] stopped");
        };
      }
    } else if (
      initializedId.current !== null &&
      initializedId.current !== id
    ) {
      stopViewer();
      if (!playablePresentationRef.current) {
        const rendererStore = new RendererStore((newProgress) =>
          setProgress(newProgress),
        );
        const playablePresentation = new PlayablePresentation(
          post.data.data,
          rendererStore,
        );
        playablePresentation.slidePlayer.setOnSlideUpdate(
          setCurrentSlideIndex,
        );
        playablePresentationRef.current = playablePresentation;
      } else {
        playablePresentationRef.current.setPresentation(post.data.data);
        playablePresentationRef.current.updateSlideTimings(
          playablePresentationRef.current.presentationRender.slides,
        );
      }

      const rendererProps: RendererProps = {
        isProduction: true,
        stop: stopCallback,
        start: (animate) => {
          playablePresentationRef.current!.slidePlayer.startLoop(animate);
        },
        renderer: playablePresentationRef.current!.renderer,
        slidePlayer: playablePresentationRef.current!.slidePlayer,
        presentation: playablePresentationRef.current!.presentationRender,
        isCoverActive,
      };

      if (
        visualStoryStore.current === null &&
        viewerRootContainerRef.current &&
        heightRef.current
      ) {
        visualStoryStore.current = new VisualStoryStore(
          viewerRootContainerRef.current,
          heightRef.current,
          bridgedEvent,
          options?.scrollSource ?? "container",
        );
      }

      visualStoryStore.current!.setPost(playablePresentationRef.current);
      renderer.current = new Renderer(
        canvasContainerRef.current!,
        htmlRendererRef.current!,
        rendererProps,
        imageOptimizer,
      );

      onResize();
      renderer.current.start();
      rendererProps.renderer.startFunction();
      visualStoryStore.current!.start(mainContainerRef.current!);
      // Reset scroll position to start screen
      visualStoryStore.current!.viewerFrame.scrollTop = 0;
      visualStoryStore.current!.scrollTop = 0;

      initializedId.current = id;
      setStarted(false);
    } else if (
      renderer.current &&
      playablePresentationRef.current &&
      initializedId.current === id
    ) {
      playablePresentationRef.current.setPresentation(post.data.data);
      playablePresentationRef.current.updateSlideTimings(
        playablePresentationRef.current.presentationRender.slides,
      );
      visualStoryStore.current?.setPost(playablePresentationRef.current);
      renderer.current.getJson(post);
    }
  }, [
    imgproxyUrl,
    fontFamilies,
    heightRef,
    id,
    isCoverActive,
    onResize,
    post.data.data,
    setCurrentSlideIndex,
    setProgress,
    setStarted,
    stopCallback,
    stopViewer,
    bridgedEvent,
    viewerRootContainerRef,
  ]);

  // Keep renderer cover gate in sync when the editor switches panels.
  useEffect(() => {
    const presentation = playablePresentationRef.current;
    if (!renderer.current || !presentation) return;
    renderer.current.setProps({
      isProduction: true,
      stop: stopCallback,
      start: (animate) => {
        presentation.slidePlayer.startLoop(animate);
      },
      renderer: presentation.renderer,
      slidePlayer: presentation.slidePlayer,
      presentation: presentation.presentationRender,
      isCoverActive,
    });
  }, [isCoverActive, options?.sentenceBloomCoverPreview, stopCallback]);

  useResizeObserver(viewerRootContainerRef, () => onResize());

  return {
    renderer,
    visualStoryStore,
    mainContainerRef,
    canvasContainerRef,
    htmlRendererRef,
    viewerRootContainerRef,
    onResize,
  };
}
